import React, { useEffect, useMemo } from "react";
import { PanelGroup } from 'react-bootstrap';
import { useTranslation } from "react-i18next";
import { Assignment } from "../../../api/Inventory/Entities/InventoryEntities";
import { MoveTypes } from "../../../api/Inventory/Entities/InventoryMovementEntities";
import Loader from "../../../components/Common/Loader";
import useInteractionGate from "../Hooks/useInteractionGate";
import useInventoryCart from "../Hooks/useInventoryCart";
import { buildCartKitReservation } from "../Shared/inventoryFormRules";
import { buildProtocolPatientDestination } from "../Shared/patientDestination";
import ProtocolItemField from "./ProtocolItemField";

export default function ProtocolConcomitantFormField({ name, disabled, onChange, medicalRecordNumber, visitId, protocolId, value }) {
    const { t } = useTranslation();
    const assignment = useMemo(() => Assignment.ProtocolAssigment(protocolId), [protocolId]);
    const dispenseValue = useMemo(() => firstDispense(value), [value]);
    const destination = useMemo(
        () => buildProtocolPatientDestination(medicalRecordNumber, visitId),
        [medicalRecordNumber, visitId]
    );
    const isPatientUnidentified = destination == null;
    const isReadOnly = disabled || isPatientUnidentified;
    const [dispenseMove, isMoveLoading, cart] = useInventoryCart(
        dispenseValue?.id ?? 0, MoveTypes.ProtocolDispense.Name,
        assignment.assignmentType, assignment.assignId,
        destination, dispenseValue
    );

    const { hasInteracted, markInteracted } = useInteractionGate();

    const handleNewItem = item => {
        markInteracted();
        cart.AddItem(item.item, item.release, item.quantity * 1, item.assignment, item.availableQuantity, item.comment);
    };
    const onRemoveItem = item => {
        markInteracted();
        cart.RemoveItem(item);
    };

    const dispenseItems = useMemo(
        () => (dispenseMove?.items ?? []).filter(i => i.originMoveItemId == null),
        [dispenseMove]
    );

    useEffect(() => {
        if (!hasInteracted()) return;
        onChange(name, [{ ...dispenseMove }]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispenseMove, name, onChange]);

    return (
        <Loader isLoading={isMoveLoading} name={name}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', backgroundColor: 'white', padding: '10px' }}>
                <div style={{ textAlign: 'center' }}>
                    <h5><strong>{t('inventory.dispense.concomitantDispenseTitle')}</strong></h5>
                    <hr />
                </div>
                {isPatientUnidentified && (
                    <p className="text-muted" style={{ textAlign: 'center' }}>
                        {t('inventory.dispense.patientVisitDataMissing')}
                    </p>
                )}
                <PanelGroup accordion id="multi-items-concomitant" defaultActiveKey="multi-item--1">
                    {dispenseItems.map((item, index) => (
                        <ProtocolItemField key={`disp-${item.uniqueId ?? index}`} index={`${index}`} movementItem={item} disabled={true}
                            assignmentType={assignment.assignmentType} assignId={assignment.assignId} itemType="Pharmacy"
                            handleSubmit={handleNewItem} handleRemove={onRemoveItem}
                            hasActions={true} actionsDisbled={isReadOnly} isDispensed={dispenseMove?.id > 0}
                            takenKits={buildCartKitReservation(dispenseItems, item)} />
                    ))}
                    {!isReadOnly && (
                        <ProtocolItemField index="-1" movementItem={null} disabled={isReadOnly}
                            assignmentType={assignment.assignmentType} assignId={assignment.assignId} itemType="Pharmacy"
                            handleSubmit={handleNewItem} bsStyle="info"
                            takenKits={buildCartKitReservation(dispenseItems)} />
                    )}
                </PanelGroup>
            </div>
        </Loader>
    );
}

function firstDispense(value) {
    if (value == null) return null;
    if (Array.isArray(value)) {
        return value.find(m => MoveTypes.IsEquivalent(m?.moveType, MoveTypes.ProtocolDispense)) ?? null;
    }
    return value;
}
